/* Levelling past level 1, following the Leveling Up rules: tier achievements at
   levels 2, 5 and 8, two advancements from the tier or below, +1 damage thresholds
   (the level adds it) and a new domain card. */

import { LEVEL_UP_TIERS } from './content/sheetText';
import { Catalog, classDomains, featureList } from './catalog';
import { companionProblems } from './companion';
import { Advancement, AdvancementKind, Character, LevelUp, Multiclass, TIER_ACHIEVEMENT_LEVELS, Trait } from './model';

export const MAX_LEVEL = 10;

export const levelOf = (c: Character) => 1 + c.levelUps.length;

export const tierOf = (level: number) => (level <= 1 ? 1 : level <= 4 ? 2 : level <= 7 ? 3 : 4);

/** Stance Fighter: the two Tier 1 stances taken at creation, then one more with each level up. */
export const knownStances = (c: Character) => [...c.stances, ...c.levelUps.flatMap((l) => (l.stance ? [l.stance] : []))];

/** Where a stance learned on the sheet goes: a free starting place (Tier 1 only), else the first level up without a stance whose tier allows it (-1: none). */
function stancePlace(c: Character, tier: number): 'start' | number {
  if (tier === 1 && c.stances.length < 2) return 'start';
  return c.levelUps.findIndex((l) => !l.stance && tier <= tierOf(l.level));
}

export const canLearnStance = (c: Character, tier: number) => stancePlace(c, tier) !== -1;

export function learnStance(c: Character, name: string, tier: number): Character {
  const place = stancePlace(c, tier);
  if (place === 'start') return { ...c, stances: [...c.stances, name] };
  return { ...c, levelUps: c.levelUps.map((l, i) => (i === place ? { ...l, stance: name } : l)) };
}

export function forgetStance(c: Character, name: string): Character {
  return {
    ...c,
    stances: c.stances.filter((s) => s !== name),
    levelUps: c.levelUps.map((l) => (l.stance === name ? { ...l, stance: undefined } : l)),
    activeStance: c.activeStance === name ? '' : c.activeStance,
  };
}

/** The level-up options in the order the guides print them (LEVEL_UP_TIERS). */
const KINDS: AdvancementKind[] = ['traits', 'hp', 'stress', 'experiences', 'domainCard', 'evasion', 'subclass', 'proficiency', 'multiclass'];

/** An extra domain card from the tier 2 and 3 lists is capped at levels 4 and 7. */
export const CARD_LEVEL_CAP: Record<number, number> = { 2: 4, 3: 7, 4: MAX_LEVEL };

export interface AdvancementOption {
  kind: AdvancementKind;
  tier: number;
  text: string;
  boxes: number;
  /** Double-boxed options take both of a level's picks. */
  wide: boolean;
  /** Boxes already marked on this tier's list. */
  used: number;
  cost: number;
  /** Crossed out by the subclass or multiclass rules. */
  crossed: boolean;
  available: boolean;
}

const advancementsOf = (c: Character) => c.levelUps.flatMap((l) => l.advancements);
const costOf = (kind: AdvancementKind) => (kind === 'proficiency' || kind === 'multiclass' ? 2 : 1);

/** How many of a level's two advancement picks are still free. */
export const picksLeft = (chosen: Advancement[]) => 2 - chosen.reduce((n, a) => n + costOf(a.kind), 0);

/**
 * What a level up still needs, in the order the flow asks for it. `multiclass` is the
 * multiclass chosen with it, if any; `companion` adds the Beastbound companion's choices.
 * Empty when the level is complete.
 */
export function levelUpProblems(l: LevelUp, multiclass: Multiclass | null, companion = false, stances = false): string[] {
  const left = picksLeft(l.advancements);
  const has = (kind: AdvancementKind, missing: (a: Advancement) => boolean) => l.advancements.some((a) => a.kind === kind && missing(a));
  return [
    TIER_ACHIEVEMENT_LEVELS.includes(l.level) && !l.experience?.trim() && 'Name your new Experience.',
    left > 0 && `Choose ${left} more advancement${left > 1 ? 's' : ''}.`,
    has('traits', (a) => (a.traits?.length ?? 0) < 2) && 'Pick two traits to raise.',
    has('experiences', (a) => (a.experiences?.length ?? 0) < 2) && 'Pick two Experiences to raise.',
    has('domainCard', (a) => !a.card) && 'Choose the extra domain card.',
    has('multiclass', () => !(multiclass?.className && multiclass.domain && multiclass.subclass)) && 'Finish your multiclass choices.',
    !l.card && 'Take your new domain card.',
    stances && !l.stance && 'Choose your new martial stance.',
    ...(companion ? companionProblems(l) : []),
  ].filter((p): p is string => !!p);
}

/**
 * Every option on the lists of `level`'s tier and below, with its marks. `chosen` are the
 * picks made so far for this level; an option is available while it has an unmarked slot,
 * is not crossed out and fits in the picks left.
 */
export function advancementOptions(c: Character, level: number, chosen: Advancement[] = []): AdvancementOption[] {
  const taken = [...advancementsOf(c), ...chosen];
  const multiclassed = !!c.multiclass || taken.some((a) => a.kind === 'multiclass');
  const upgrades = taken.filter((a) => a.kind === 'subclass').length;
  const left = picksLeft(chosen);
  return LEVEL_UP_TIERS.filter((t) => t.tier <= tierOf(level)).flatMap((t) =>
    t.options.map((o, i) => {
      const kind = KINDS[i];
      const marks = taken.filter((a) => a.kind === kind && a.tier === t.tier).length;
      const crossed =
        (kind === 'subclass' && (upgrades >= 2 || taken.some((a) => a.kind === 'multiclass' && a.tier === t.tier))) ||
        (kind === 'multiclass' && (multiclassed || taken.some((a) => a.kind === 'subclass' && a.tier === t.tier)));
      const full = o.wide ? marks > 0 : marks >= o.boxes;
      const cost = costOf(kind);
      return {
        kind, tier: t.tier, text: o.text, boxes: o.boxes, wide: !!o.wide,
        used: o.wide ? Math.min(marks * 2, o.boxes) : marks,
        cost, crossed, available: !crossed && !full && cost <= left,
      };
    }),
  );
}

/** The Experiences in sheet order: the two from creation, then one per tier achievement. */
export function experienceList(c: Character): Array<{ name: string; bonus: number }> {
  const names = [...c.experiences, ...c.levelUps.filter((l) => l.experience !== undefined).map((l) => l.experience ?? '')];
  const raised = advancementsOf(c).filter((a) => a.kind === 'experiences');
  return names.map((name, i) => ({
    name: name.trim(),
    bonus: 2 + (c.purposeful === i ? 1 : 0) + raised.filter((a) => a.experiences?.includes(i)).length,
  }));
}

/** Traits raised since their marks were last cleared (at levels 5 and 8). */
export function markedTraits(c: Character): Trait[] {
  const level = levelOf(c);
  const cleared = Math.max(0, ...TIER_ACHIEVEMENT_LEVELS.filter((l) => l > 2 && l <= level));
  return c.levelUps
    .filter((l) => l.level >= cleared)
    .flatMap((l) => l.advancements)
    .flatMap((a) => (a.kind === 'traits' ? a.traits ?? [] : []));
}

/** Domain cards owned: creation picks, then each level's cards, with exchanges applied. */
export function ownedCards(c: Character): string[] {
  let cards = [...c.domainCards];
  c.levelUps.forEach((l) => {
    cards.push(...l.advancements.flatMap((a) => (a.kind === 'domainCard' && a.card ? [a.card] : [])));
    if (l.card) cards.push(l.card);
    const swap = l.swap;
    if (swap) cards = cards.map((name) => (name === swap.from ? swap.to : name));
  });
  return cards;
}

export interface SubclassCard {
  tier: 'Foundation' | 'Specialization' | 'Mastery';
  className: string;
  subclass: string;
  spellcastTrait?: string;
  features: ReturnType<typeof featureList>;
}

/** The subclass cards held: foundation, then one more per "upgraded subclass" advancement. */
export function subclassCards(c: Character, catalog: Catalog): SubclassCard[] {
  const cls = catalog.cls(c.className);
  const sub = cls?.subclasses?.[c.subclass];
  if (!cls || !sub) return [];
  const upgrades = advancementsOf(c).filter((a) => a.kind === 'subclass').length;
  const card = (tier: SubclassCard['tier'], list: Parameters<typeof featureList>[0]): SubclassCard => ({
    tier, className: cls.name, subclass: sub.name, spellcastTrait: sub.spellcastTrait, features: featureList(list),
  });
  return [
    card('Foundation', sub.foundation),
    ...(upgrades >= 1 ? [card('Specialization', sub.specialization)] : []),
    ...(upgrades >= 2 ? [card('Mastery', sub.mastery)] : []),
  ];
}

/** The multiclass foundation card, when the character has multiclassed. */
export function multiclassCard(c: Character, catalog: Catalog): SubclassCard | null {
  const cls = c.multiclass && catalog.cls(c.multiclass.className);
  const sub = cls && c.multiclass ? cls.subclasses?.[c.multiclass.subclass] : undefined;
  if (!cls || !sub) return null;
  return { tier: 'Foundation', className: cls.name, subclass: sub.name, spellcastTrait: sub.spellcastTrait, features: featureList(sub.foundation) };
}

/**
 * Domain cards that can be taken on reaching `level`: the class's domains up to `cap`,
 * and a multiclass domain up to half the level (rounded up). Owned cards are left out.
 */
export function cardChoices(c: Character, catalog: Catalog, level: number, cap = level) {
  const owned = new Set(ownedCards(c));
  const own = catalog.cardsIn(classDomains(catalog.cls(c.className))).filter((k) => k.level <= Math.min(level, cap));
  const multi = c.multiclass ? catalog.cardsIn([c.multiclass.domain]).filter((k) => k.level <= Math.ceil(level / 2)) : [];
  return [...own, ...multi].filter((k) => !owned.has(k.name));
}
